import React, { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Button, Checkbox, Empty, Input, Layout, Skeleton, Tag, message } from 'antd';
import {
  ClockCircleOutlined, EnvironmentOutlined, EyeOutlined,
  LikeFilled, LikeOutlined, MessageOutlined, TeamOutlined, UserOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import MainHeader from '@/components/mainheader';
import MainFooter from '@/components/mainfooter';
import PageTopbar from '@/components/pagetopbar';
import PageBack from '@/components/pageback';
import usePageBack from '@/utils/usePageBack';
import { useSyncBrowsePost } from '@/utils/browseState';
import PostStatus from '@/components/poststatus';
import { applicationColors } from '@/utils/applications';
import PostEditorDialog from './components/PostEditorDialog';
import ActivityPanel from './components/ActivityPanel';
import PostActionMenus from './components/PostActionMenus';
import ReopenPost from './components/ReopenPost';
import ExtendPost from './components/ExtendPost';
import PostPinAction from './components/PostPinAction';
import PostBadges from '@/components/postbadges';
import FavoriteButton from '@/components/favoritebutton';
import PostVisitorMenu from './components/PostVisitorMenu';
import CommentThreads, { commentPage } from './components/CommentThreads';
import ReplyComposer from './components/ReplyComposer';
import PostImages from './components/PostImages';
import useViewedNotification from '@/utils/useViewedNotification';
import ApplicationActions from '@/components/applicationactions';
import PostSchoolBinding from '@/components/postschoolbinding';
import UserProfileLink from '@/components/userprofilelink';
import {
  createPostComment, fetchCommentContext, fetchMyApplications, fetchPostComments, fetchPostDetail,
  getPostErrorMessage, togglePostLike, fetchPostConversation, closePost, deletePost,
} from '@/apis/posts';
import { createPrivateConversation } from '@/apis/chat';
import { syncPostMetrics } from '@/store/modules/posts';
import './index.scss';

const { Content } = Layout;
const { TextArea } = Input;

const formatDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

const applicationDescriptions = {
  pending: '申请已提交，正在等待发布者审批。',
  approved: '申请已通过，请留意活动安排。',
  rejected: '本次申请未通过，可查看下方可用操作。',
  withdrawn: '申请已撤回，符合招募条件时可重新申请。',
};

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const goBack = usePageBack('/');
  const syncBrowsePost = useSyncBrowsePost();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const targetId = /^[1-9]\d*$/.test(query.get('comment') || '') ? query.get('comment') : null;
  const notificationId = query.get('notification');
  const dispatch = useDispatch();
  const { isAuthenticated, userInfo } = useSelector(state => state.user);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsPagination, setCommentsPagination] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [newComment, setNewComment] = useState(null);
  const [ownApplication, setOwnApplication] = useState(null);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [applicationError, setApplicationError] = useState('');
  const [focusPath, setFocusPath] = useState([]);
  const [locatedTarget, setLocatedTarget] = useState(null);
  const [locatingError, setLocatingError] = useState('');
  const rootRequest = useRef(0);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [postError, setPostError] = useState('');
  const [commentsError, setCommentsError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [commentRetryKey, setCommentRetryKey] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [liking, setLiking] = useState(false);
  const [commenting, setCommenting] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [acceptRecruitment, setAcceptRecruitment] = useState(false);
  const [openingChat, setOpeningChat] = useState(false);
  const [joinedRoom, setJoinedRoom] = useState(null);
  const [editing, setEditing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [extending, setExtending] = useState(false);
  const [postBusy, setPostBusy] = useState(false);
  const postOperationRef = useRef(false);
  const chatLock = useRef(false);
  const likingRef = useRef(false);
  const commentingRef = useRef(false);
  const validPostId = /^[1-9]\d*$/.test(postId || '');
  const loadedPostId = post?.id;

  useEffect(() => {
    if (post) syncBrowsePost(post.id, post);
  }, [post, syncBrowsePost]);

  useEffect(() => {
    if (!validPostId) {
      setPost(null);
      setPostError('帖子地址无效');
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setPost(null);
    setComments([]);
    setPostError('');
    fetchPostDetail(postId)
      .then(result => {
        if (!active) return;
        if (!result || !result.id) throw new Error('帖子详情格式不正确');
        setPost(result);
        dispatch(syncPostMetrics({
          postId,
          post: result,
          like_count: result.like_count,
          comment_count: result.comment_count,
          is_liked: result.is_liked,
        }));
      })
      .catch(error => {
        if (active) setPostError(error?.response?.status === 404
          ? '帖子不存在或已被删除'
          : getPostErrorMessage(error, '帖子加载失败，请稍后重试'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [dispatch, postId, retryKey, validPostId, isAuthenticated, userInfo?.id]);

  useEffect(() => {
    if (!validPostId || !loadedPostId) return;
    let active = true;
    setCommentsError('');
    setComments([]); setCommentsPagination(null);
    setCommentsLoading(true);
    const version = ++rootRequest.current;
    setReplyTarget(null); setReplyDrafts({}); setNewComment(null); setFocusPath([]); setLocatedTarget(null);
    fetchPostComments(postId, { parent_id: 'null', page: 1, pageSize: 10 })
      .then(commentPage).then(result => {
        if (!active) return;
        if (version !== rootRequest.current) return;
        setComments(result.data); setCommentsPagination(result.pagination);
      })
      .catch(error => {
        if (active) setCommentsError(getPostErrorMessage(error, '评论加载失败，请稍后重试'));
      })
      .finally(() => {
        if (active) setCommentsLoading(false);
      });
    return () => { active = false; rootRequest.current += 1; };
  }, [postId, validPostId, isAuthenticated, userInfo?.id, commentRetryKey, loadedPostId]);

  useEffect(() => {
    if (!loadedPostId || commentsLoading || !targetId || locatedTarget === targetId) return;
    let active = true;
    setLocatingError('');
    const locate = async () => {
      try {
        const result = await fetchCommentContext(postId, targetId);
        const path = result?.path;
        if (!Array.isArray(path) || !path.length || path.length > 32 || String(path[path.length - 1].id) !== String(targetId)) throw new Error('评论回复关系无效');
        if (!active) return;
        setFocusPath(path);
        setComments(current => current.some(item => item.id === path[0].id) ? current : [path[0], ...current]);
        setLocatedTarget(targetId);
      } catch (err) {
        if (active) { setFocusPath([]); setLocatedTarget(null); setLocatingError(getPostErrorMessage(err, '这条评论已不存在，或你没有查看权限')); }
      }
    };
    locate();
    return () => { active = false; };
  }, [postId, loadedPostId, commentsLoading, targetId, locatedTarget, isAuthenticated, userInfo?.id]);
  useViewedNotification(notificationId, Boolean(post && !loading && (!targetId || locatedTarget === targetId)));
  const moreComments = async () => {
    if (commentsLoading) return;
    const version = ++rootRequest.current;
    setCommentsLoading(true); setCommentsError('');
    try {
      const result = commentPage(await fetchPostComments(postId, { parent_id: 'null', page: commentsPagination.page + 1, pageSize: 10 }));
      if (version === rootRequest.current) {
        setComments(current => [...new Map([...current, ...result.data].map(item => [item.id, item])).values()]);
        setCommentsPagination(result.pagination);
      }
    } catch (err) { if (version === rootRequest.current) setCommentsError(getPostErrorMessage(err, '评论加载失败')); }
    finally { if (version === rootRequest.current) setCommentsLoading(false); }
  };
  const chooseReply = comment => {
    if (commentingRef.current) return;
    if (!isAuthenticated) return requireLogin('回复评论');
    setReplyTarget(comment);
  };

  const requireLogin = action => {
    message.warning(`请先登录后${action}`);
    navigate('/login');
  };

  const handleLike = async () => {
    if (!isAuthenticated) return requireLogin('点赞');
    if (likingRef.current) return;
    likingRef.current = true;
    setLiking(true);
    try {
      const result = await togglePostLike(postId);
      if (typeof result?.is_liked !== 'boolean' || typeof result?.like_count !== 'number') {
        throw new Error('点赞响应格式不正确');
      }
      setPost(current => current && ({ ...current, is_liked: result.is_liked, like_count: result.like_count }));
      dispatch(syncPostMetrics({ postId, is_liked: result.is_liked, like_count: result.like_count }));
      message.success(result.is_liked ? '点赞成功' : '已取消点赞');
    } catch (error) {
      message.error(getPostErrorMessage(error, '点赞失败，请稍后重试'));
    } finally {
      likingRef.current = false;
      setLiking(false);
    }
  };

  const handleComment = async (reply = false) => {
    if (!isAuthenticated) return requireLogin('评论');
    const target = reply ? replyTarget : null;
    if (reply && !target) return;
    const draft = target ? replyDrafts[target.id] : null;
    const content = (target ? draft?.text || '' : commentText).trim();
    if (!content) return message.warning('请输入评论内容');
    if (commentingRef.current) return;
    commentingRef.current = true;
    setCommenting(true);
    try {
      const applying = !target && acceptRecruitment && canApply;
      const source = location.state?.recommendation;
      const attributed = applying && source && String(source.postId) === String(postId) && String(source.userId) === String(userInfo?.id)
        && Date.parse(source.createdAt) + 7 * 86400000 > Date.now();
      const result = await createPostComment(postId, content, { is_public: target ? target.is_public === false ? false : draft?.isPublic ?? true : isPublic, accept_recruitment: applying,
        ...(attributed ? { recommendation_item_id: source.itemId } : {}), ...(target ? { parent_id: target.id } : {}) });
      if (!result?.id) throw new Error('评论响应格式不正确');
      if (result.recruitment_application) setOwnApplication({ ...result.recruitment_application, post: Number(postId), post_owner: post.user });
      if (target) setNewComment(result);
      else setComments(current => [result, ...current]);
      if (target) {
        setReplyTarget(null);
        setReplyDrafts(current => { const next = { ...current }; delete next[target.id]; return next; });
      } else {
        setIsPublic(true); setCommentText(''); setAcceptRecruitment(false);
      }
      setPost(current => current && ({ ...current, comment_count: (current.comment_count || 0) + 1 }));
      dispatch(syncPostMetrics({ postId, comment_count: (post?.comment_count || 0) + 1 }));
      setCommentsError('');
      message.success(result.recruitment_application ? '申请已提交，请等待帖子作者审批' : '评论发布成功');
    } catch (error) {
      message.error(getPostErrorMessage(error, '评论发布失败，请稍后重试'));
    } finally {
      commentingRef.current = false;
      setCommenting(false);
    }
  };

  const author = post?.user?.nickname || post?.user?.username || '用户';
  const owner = Boolean(isAuthenticated && userInfo?.id && String(userInfo.id) === String(post?.user?.id));
  const performPostOperation = async deleting => {
    if (!owner || postOperationRef.current || postBusy) return false;
    postOperationRef.current = true; setPostBusy(true);
    try {
      if (deleting) {
        await deletePost(postId);
        syncBrowsePost(postId, null);
        dispatch(syncPostMetrics({ postId, removed: true }));
        message.success('帖子已删除');
        if (window.history.state?.idx > 0) navigate(-1);
        else navigate('/', { replace: true });
      } else {
        await closePost(postId);
        message.success('帖子已关闭');
        setRetryKey(value => value + 1);
      }
      return true;
    } catch (error) {
      message.error(getPostErrorMessage(error, deleting ? '删除失败，请稍后重试' : '关闭失败，请稍后重试'));
      return false;
    } finally { postOperationRef.current = false; setPostBusy(false); }
  };
  const myApplication = ownApplication;
  const canApply = isAuthenticated && !owner && !applicationLoading && !applicationError && !myApplication && !joinedRoom && post?.status === 'active' && post?.is_recruiting && new Date(post.expire_time) > new Date();
  useEffect(() => {
    let active = true;
    setOwnApplication(null); setApplicationError('');
    if (!loadedPostId || !isAuthenticated || owner) { setApplicationLoading(false); return; }
    setApplicationLoading(true);
    fetchMyApplications({ post_id: postId, page: 1, pageSize: 1 }).then(result => {
      if (!Array.isArray(result?.data)) throw new Error('申请状态响应无效');
      if (active) setOwnApplication(result.data[0] || null);
    }).catch(err => { if (active) setApplicationError(getPostErrorMessage(err, '申请状态加载失败，请刷新重试')); })
      .finally(() => { if (active) setApplicationLoading(false); });
    return () => { active = false; };
  }, [postId, loadedPostId, isAuthenticated, owner, userInfo?.id, retryKey]);
  useEffect(() => {
    let active = true;
    setJoinedRoom(null);
    if ((post?.is_recruiting || post?.completed_at || post?.approved_participant_count > 0 || myApplication?.status === 'approved') && isAuthenticated && userInfo?.id && loadedPostId) {
      // 分页评论未必包含自己的旧申请，通过后端成员权限确认是否已入群。
      const check = async () => {
        try { const room = await fetchPostConversation(postId); if (active && room?.id) setJoinedRoom(room); }
        catch { /* 尚未入群属于正常状态，点击聊天时仍由后端检查权限。 */ }
      };
      check();
    }
    return () => { active = false; };
  }, [postId, loadedPostId, post?.is_recruiting, post?.completed_at, post?.approved_participant_count, myApplication?.status, isAuthenticated, userInfo?.id, retryKey]);
  const applicationLabels = { pending: '申请待审批', approved: '申请已同意', rejected: '申请已拒绝', withdrawn: '申请已撤回' };
  const openChat = async group => {
    if (!isAuthenticated) return requireLogin('聊天');
    if (chatLock.current) return;
    chatLock.current = true; setOpeningChat(true);
    try {
      const room = group ? await fetchPostConversation(postId) : await createPrivateConversation(post.user.id);
      if (!room?.id) throw new Error('会话响应无效');
      navigate(`/message/${room.id}`);
    } catch (error) {
      message.error(group && error?.response?.status === 404
        ? '帖子群聊尚未创建，或你还不是群成员'
        : getPostErrorMessage(error, group ? '帖子群聊尚未生成，或你还不是成员' : '发起私聊失败'));
    }
    finally { chatLock.current = false; setOpeningChat(false); }
  };
  const renderReply = comment => {
    if (!isAuthenticated || String(replyTarget?.id) !== String(comment.id)) return null;
    const draft = replyDrafts[comment.id] || { text: '', isPublic: comment.is_public !== false };
    const updateDraft = values => setReplyDrafts(current => ({
      ...current,
      [comment.id]: { text: '', isPublic: comment.is_public !== false, ...current[comment.id], ...values },
    }));
    return <ReplyComposer key={comment.id} comment={comment} user={userInfo} text={draft.text}
      isPublic={comment.is_public === false ? false : draft.isPublic} busy={commenting}
      onTextChange={text => updateDraft({ text })} onPrivacyChange={value => updateDraft({ isPublic: value })}
      onCancel={() => setReplyTarget(null)} onSubmit={() => handleComment(true)} />;
  };

  return (
    <Layout className="post-detail-page">
      <MainHeader />
      <PageTopbar className="post-detail-mobile-topbar" title="帖子详情" backLabel="返回" onBack={goBack} />
      <PageBack className="post-detail-back" onBack={goBack} />
      <Content className="post-detail-content">
        <div className="post-detail-shell pageBack-shell">
          {loading && <div className="post-detail-panel"><Skeleton active avatar paragraph={{ rows: 5 }} /></div>}
          {!loading && postError && (
            <Alert type="error" showIcon message={postError}
              action={<Button size="small" onClick={() => setRetryKey(value => value + 1)}>重试</Button>} />
          )}

          {!loading && post && (
            <>
              <ActivityPanel key={`${post.id}:${userInfo?.id}`} post={post} owner={owner} participant={ownApplication?.status === 'approved'} application={ownApplication} userId={userInfo?.id}
                enabled={Boolean(post.is_recruiting || post.completed_at || ownApplication?.status === 'approved')} onChanged={() => setRetryKey(n => n + 1)}>
                {activityActions => (
                  <article className="post-detail-panel">
                    <div className="post-detail-author">
                      <UserProfileLink user={post.user}><Avatar size={44} src={post.user?.avatar || undefined} icon={<UserOutlined />} /></UserProfileLink>
                      <div className="post-detail-author-name">
                        <div className="postAuthorIdentity"><UserProfileLink user={post.user}><strong>{author}</strong></UserProfileLink><PostBadges post={post} showPinned={false} /></div>
                        <time dateTime={post.created_at}>{formatDateTime(post.created_at)} 发布</time>
                        <span className="post-detail-uid">UID {post.user?.id}</span>
                      </div>
                      <PostStatus post={post} className="post-detail-status" />
                    </div>

                    <PostBadges post={post} className="post-detail-badges" showSchool={false} />
                    <h1>{post.title}</h1>
                    <p className="post-detail-description">{post.description}</p>
                    {post.is_recruiting && post.post_type === 'skill_exchange' && <div className="post-detail-facts post-detail-skills"><span>提供技能：{post.offered_skills}</span><span>希望交换：{post.wanted_skills}</span></div>}

                    <PostImages images={post.images || []} title={post.title} />

                    {post.tags?.length > 0 && (
                      <div className="post-detail-tags">
                        {post.tags.map(tag => <Tag key={tag.id}>{tag.name}</Tag>)}
                      </div>
                    )}

                    <div className="post-detail-facts">
                      {post.is_recruiting && post.location && <span><EnvironmentOutlined />活动地点：{post.location}</span>}
                      {post.is_recruiting && post.max_participants != null && (
                        <span><TeamOutlined />招募人数：{post.max_participants} 人，已加入 {post.approved_participant_count || 0} 人</span>
                      )}
                      {post.expire_time && <span><ClockCircleOutlined />截止时间：{formatDateTime(post.expire_time)}</span>}
                      <span><EyeOutlined />浏览 {post.view_count || 0}</span>
                    </div>

                    <div className="post-detail-actions">
                      <Button className="post-detail-like-button" type={post.is_liked ? 'primary' : 'default'}
                        icon={post.is_liked ? <LikeFilled /> : <LikeOutlined />}
                        loading={liking} onClick={handleLike} aria-pressed={Boolean(post.is_liked)}>
                        {post.is_liked ? '已点赞' : '点赞'} {post.like_count || 0}
                      </Button>
                      <FavoriteButton key={`${isAuthenticated}:${userInfo?.id}:${post.id}`} post={post} onChange={value => setPost(current => ({ ...current, is_favorited: value }))} />
                      <span><MessageOutlined /> 评论 {post.comment_count || 0}</span>
                      <PostActionMenus post={post} owner={owner} activityItems={activityActions} postBusy={postBusy} chatBusy={openingChat}
                        visitorActions={<PostPinAction key={`${isAuthenticated}:${userInfo?.id}:${post.id}`} post={post} onChanged={result => setPost(current => current && ({ ...current, ...result }))}>
                          {(pinItem, pinBusy) => <PostVisitorMenu post={post} chatBusy={openingChat} onContact={() => openChat(false)} adminItems={pinItem ? [pinItem] : []} adminBusy={pinBusy} />}
                        </PostPinAction>}
                        canManage={owner && Boolean(post.is_recruiting || post.completed_at || post.approved_participant_count > 0)}
                        canEnterGroup={Boolean(joinedRoom || ownApplication?.conversation_id || (owner && (post.is_recruiting || post.completed_at || post.approved_participant_count > 0)))}
                        onManage={() => navigate(`/posts/${postId}/manage`)} onGroup={() => openChat(true)} onEdit={() => setEditing(true)}
                        onClose={() => performPostOperation(false)} onDelete={() => performPostOperation(true)} onReopen={() => setReopening(true)} onExtend={() => setExtending(true)} />
                    </div>
                  </article>
                )}
              </ActivityPanel>
              {owner && <PostSchoolBinding post={post} onChanged={() => setRetryKey(n => n + 1)} />}
              {applicationError && <Alert type="warning" title={applicationError} action={<Button onClick={() => setRetryKey(n => n + 1)}>重试</Button>} />}
              {ownApplication && <section className="post-detail-panel post-detail-application" aria-labelledby="post-application-title">
                <div className="post-detail-application-summary">
                  <span className="post-detail-application-icon" aria-hidden="true"><TeamOutlined /></span>
                  <div className="post-detail-application-info">
                    <div className="post-detail-application-heading">
                      <h2 id="post-application-title">我的申请</h2>
                      <Tag className="post-detail-application-state" color={applicationColors[ownApplication.status] || 'default'}>{applicationLabels[ownApplication.status] || '申请已提交'}</Tag>
                    </div>
                    <p>{applicationDescriptions[ownApplication.status] || '请查看当前可用的申请操作。'}</p>
                  </div>
                </div>
                <ApplicationActions application={ownApplication} onChanged={() => setRetryKey(n => n + 1)} />
              </section>}
              <section className="post-detail-panel post-detail-comments" aria-labelledby="post-comments-title">
                <h2 id="post-comments-title">评论 <span>{post.comment_count || 0}</span></h2>
                <div className="post-detail-composer">
                  <TextArea value={commentText} disabled={commenting} onChange={event => setCommentText(event.target.value)}
                    maxLength={500} showCount autoSize={{ minRows: 3, maxRows: 8 }} placeholder="说说你的想法，和大家交流一下…"
                    aria-label="评论内容" />
                  <div className="post-detail-submit">
                    {isAuthenticated && <div className="post-detail-comment-options"><Checkbox checked={!isPublic} disabled={commenting} onChange={event => setIsPublic(!event.target.checked)}>私密评论（仅作者和自己可见）</Checkbox>{canApply && <Checkbox checked={acceptRecruitment} disabled={commenting} onChange={event => setAcceptRecruitment(event.target.checked)}>同时申请加入招募</Checkbox>}</div>}
                    {!isAuthenticated && <span>登录后可以发表评论</span>}
                    <div className="post-detail-composer-actions">
                      <Button type="primary" loading={commenting} onClick={() => handleComment()}
                        disabled={commentsLoading || (isAuthenticated && !commentText.trim())}>发表评论</Button>
                    </div>
                  </div>
                </div>

                {commentsError && <Alert type="warning" showIcon message={commentsError}
                  action={<Button size="small" onClick={() => setCommentRetryKey(value => value + 1)}>重试</Button>} />}
                {commentsLoading && <Skeleton active paragraph={{ rows: 2 }} />}
                {!commentsLoading && !commentsError && comments.length === 0 && <Empty description="还没有评论，来发表第一条吧" />}
                {locatingError && <Alert type="warning" showIcon title={locatingError} />}
                <CommentThreads key={`${postId}:${isAuthenticated}:${userInfo?.id}:${commentRetryKey}`} comments={comments} postId={postId} onReply={chooseReply}
                  newComment={newComment} focusPath={focusPath} targetId={targetId} showRecruitment={Boolean(post.is_recruiting)} renderReply={renderReply} />
                {!commentsLoading && commentsPagination?.page < commentsPagination?.totalPages && <Button onClick={moreComments}>加载更多评论</Button>}
              </section>
            </>
          )}
        </div>
      </Content>
      {owner && <PostEditorDialog open={editing} post={post} busy={postBusy} onBusyChange={setPostBusy}
        onCancel={() => { setEditing(false); setRetryKey(value => value + 1); }} onSaved={() => { setEditing(false); setRetryKey(value => value + 1); }} />}
      <MainFooter />
      {owner && reopening && <ReopenPost key={`${userInfo?.id}:${post.id}`} post={post} onCancel={() => setReopening(false)} onSaved={() => { setReopening(false); setRetryKey(value => value + 1); }} />}
      {owner && extending && <ExtendPost key={`${userInfo?.id}:${post.id}`} post={post} onCancel={() => setExtending(false)} onSaved={result => {
        setPost(current => current && ({ ...current, ...result }));
        dispatch(syncPostMetrics({ postId, post: result }));
        setExtending(false);
      }} />}
    </Layout>
  );
};

export default PostDetail;
